/**
 * §7.27 Mass-Gathering Risk Module — Phase 10 Tier-S #2.
 *
 * Religious + civic mass gatherings concentrate susceptibles + travel +
 * close contact. The calendar is predictable, so PaCC can pre-warn —
 * 60/30/14-day countdown banners with pathogen-specific risk priors per
 * the cultural-risk register's mass-gathering domain.
 *
 * Sample data is illustrative (the actual mass-gathering calendar is in
 * the cultural-risk register seeded by Phase 9.2a). v1 lists the major
 * known recurring events with their pathogen-specific risk profile.
 *
 * Operator workflow:
 *   - At T-60d: situational-awareness chip on related surfaces
 *   - At T-30d: pre-event risk-assessment briefing pre-loaded
 *   - At T-14d: enhanced surveillance recommendation
 *   - At T+0  : real-time monitoring active
 *   - At T+14d: post-event dispersal modelling (who's gone home where)
 */

import { useMemo, useState } from 'react';
import { auditCapture } from '../audit/capture';
import { PinToBriefButton } from '../pack/PinToBriefButton';

interface MassGatheringEvent {
  id: string;
  /** Event name. */
  name: string;
  /** Geography description (city / country). */
  location: string;
  iso3: string;
  /** Next occurrence ISO date. */
  nextDate: string;
  /** Expected attendance (peak or cumulative). */
  attendance: string;
  /** Pathogen-specific risk priors (transmission-class → risk multiplier). */
  riskProfile: Array<{ pathogenClass: string; multiplier: number; rationale: string }>;
  /** Historical precedent — citation. */
  precedent: string;
}

const SAMPLE_EVENTS: readonly MassGatheringEvent[] = [
  {
    id: 'hajj-2026',
    name: 'Hajj',
    location: 'Mecca, Saudi Arabia',
    iso3: 'SAU',
    nextDate: '2026-05-26',
    attendance: '~2M pilgrims · 5d concentration',
    riskProfile: [
      { pathogenClass: 'respiratory-droplet', multiplier: 5, rationale: 'extreme density + multi-country mixing' },
      { pathogenClass: 'STI-BBV',             multiplier: 1, rationale: 'low risk profile' },
      { pathogenClass: 'zoonotic-spillover',  multiplier: 3, rationale: 'camel-MERS interface + raw camel milk consumption' },
    ],
    precedent: 'Meningococcal ACWY mandate post-2000 W135 outbreak; MERS-CoV monitoring since 2012',
  },
  {
    id: 'kumbh-mela-2027',
    name: 'Kumbh Mela',
    location: 'Allahabad / Prayagraj, India',
    iso3: 'IND',
    nextDate: '2027-01-14',
    attendance: '~120M cumulative footfall · 45d',
    riskProfile: [
      { pathogenClass: 'respiratory-droplet', multiplier: 4, rationale: '2021 Haridwar event credited as Delta-variant accelerator (contested but plausible)' },
      { pathogenClass: 'faecal-oral',         multiplier: 3, rationale: 'massive footfall + WASH-pressure at riverine bathing sites' },
      { pathogenClass: 'vector-borne',        multiplier: 1.5, rationale: 'monsoon-overlap dengue risk in northern India' },
    ],
    precedent: 'ICMR + Lancet correspondence on 2021 Haridwar Delta amplification',
  },
  {
    id: 'arbaeen-2026',
    name: 'Arbaeen Pilgrimage',
    location: 'Karbala, Iraq',
    iso3: 'IRQ',
    nextDate: '2026-08-23',
    attendance: '~15-25M pilgrims · 2wk',
    riskProfile: [
      { pathogenClass: 'respiratory-droplet', multiplier: 4, rationale: 'extreme density + multi-country mixing (Iran, Pakistan, India, Lebanon, GCC)' },
      { pathogenClass: 'faecal-oral',         multiplier: 4, rationale: 'cholera + typhoid risk; WASH-pressure on rural Karbala infrastructure' },
      { pathogenClass: 'vector-borne',        multiplier: 2, rationale: 'leishmaniasis-endemic region' },
    ],
    precedent: 'Iraqi MoH + WHO EMRO issue pre-Arbaeen risk assessments annually',
  },
  {
    id: 'chunyun-2027',
    name: 'Chunyun (Chinese New Year travel)',
    location: 'China (nationwide)',
    iso3: 'CHN',
    nextDate: '2027-02-06',
    attendance: '~3B person-trips · 40d',
    riskProfile: [
      { pathogenClass: 'respiratory-droplet', multiplier: 6, rationale: 'largest annual human migration; COVID 2020 amplifier' },
      { pathogenClass: 'zoonotic-spillover',  multiplier: 2, rationale: 'wet-market + live-poultry exposure during festive consumption' },
    ],
    precedent: 'COVID 2020 international seeding via Wuhan-origin travel during chunyun',
  },
];

type Phase = 'monitor' | 't-60d' | 't-30d' | 't-14d' | 'in-progress' | 't+14d' | 'past';

function getPhase(nextDateIso: string, today: Date = new Date()): Phase {
  const event = new Date(nextDateIso);
  const days = Math.round((event.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (days > 60) return 'monitor';
  if (days > 30) return 't-60d';
  if (days > 14) return 't-30d';
  if (days > 0)  return 't-14d';
  if (days > -14) return 'in-progress';
  if (days > -30) return 't+14d';
  return 'past';
}

const PHASE_META: Record<Phase, { word: string; cssVar: string; action: string }> = {
  monitor:       { word: 'Monitor',       cssVar: 'var(--bm-text-subtle)',                   action: 'Routine watching brief' },
  't-60d':       { word: 'T-60d',         cssVar: 'var(--bm-accent, #6ea3ff)',               action: 'Situational-awareness chip on related surfaces' },
  't-30d':       { word: 'T-30d',         cssVar: 'var(--bm-threat-3, #fbbf24)',             action: 'Pre-event risk-assessment briefing pre-loaded' },
  't-14d':       { word: 'T-14d',         cssVar: 'var(--bm-threat-2, #fb923c)',             action: 'Enhanced surveillance recommendation; lab capacity pre-positioning' },
  'in-progress': { word: 'In progress',   cssVar: 'var(--bm-threat-1, #f87171)',             action: 'Real-time monitoring active; daily situation reports' },
  't+14d':       { word: 'T+14d dispersal', cssVar: 'var(--bm-threat-3, #fbbf24)',           action: 'Post-event dispersal modelling — who went home where' },
  past:          { word: 'Past',          cssVar: 'var(--bm-text-subtle)',                   action: 'Archive; AAR pending' },
};

export function MassGatheringCalendar() {
  const [selected, setSelected] = useState<string | null>(null);
  const events = useMemo(
    () => [...SAMPLE_EVENTS].sort((a, b) => Date.parse(a.nextDate) - Date.parse(b.nextDate)),
    [],
  );
  const handleSelect = (id: string) => {
    const next = selected === id ? null : id;
    setSelected(next);
    if (next) {
      auditCapture.emit({
        event: { type: 'surface.view', surface: '§7.27', subjectIds: [id], payload: { event: 'mass-gathering-expand' } },
      });
    }
  };
  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.27</span>
        <h1 className="surface__title">Mass-Gathering Risk Calendar</h1>
      </div>
      <div className="surface__sub" style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
      }}>
        <span>
          Predictable mass gatherings (Hajj · Kumbh Mela · Arbaeen · chunyun ·
          civic + sporting events) with pathogen-class risk priors per the
          cultural-risk register. Pre-warn at T-60 / T-30 / T-14 with the
          appropriate operator action.
        </span>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 700,
          padding: '2px 6px', borderRadius: 4,
          color: 'var(--bm-state-stale)',
          background: 'var(--bm-prov-stale-bg)',
          border: '1px solid var(--bm-state-stale)',
        }}>⚠ EDU · ILLUSTRATIVE SAMPLE</span>
      </div>

      <div style={{ marginTop: 16, display: 'grid', gap: 10 }}>
        {events.map(ev => {
          const phase = getPhase(ev.nextDate);
          const meta = PHASE_META[phase];
          const isSel = selected === ev.id;
          const days = Math.round((Date.parse(ev.nextDate) - Date.now()) / (1000 * 60 * 60 * 24));
          return (
            <div key={ev.id} style={{
              background: 'var(--bm-bg-panel, var(--bm-bg))',
              border: '1px solid var(--bm-border)',
              borderLeft: `3px solid ${meta.cssVar}`,
              borderRadius: 'var(--bm-radius-md)',
              padding: 14,
            }}>
              <button
                type="button"
                onClick={() => handleSelect(ev.id)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 2fr) 110px 130px auto auto',
                  gap: 10, alignItems: 'center',
                  width: '100%', textAlign: 'left' as const,
                  border: 'none', background: 'transparent', cursor: 'pointer',
                  padding: 0,
                }}
              >
                <span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--bm-text)' }}>{ev.name}</span>
                  <span style={{ fontSize: 11, color: 'var(--bm-text-subtle)', marginLeft: 8 }}>
                    {ev.location} · {ev.iso3}
                  </span>
                </span>
                <span style={{
                  fontFamily: 'var(--bm-font-mono)', fontSize: 11,
                  color: meta.cssVar, fontWeight: 700,
                }}>{meta.word}</span>
                <span style={{
                  fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-muted)',
                }}>{days > 0 ? `T-${days}d` : days < 0 ? `T+${-days}d` : 'TODAY'}</span>
                <span style={{
                  fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-subtle)',
                }}>{ev.nextDate}</span>
                <span style={{ color: 'var(--bm-text-subtle)', fontSize: 12 }}>{isSel ? '▾' : '▸'}</span>
              </button>
              {isSel && (
                <div style={{
                  marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--bm-border)',
                  display: 'grid', gap: 10,
                }}>
                  <div style={{ fontSize: 12, color: 'var(--bm-text-muted)' }}>
                    <b style={{ color: 'var(--bm-text)' }}>Expected attendance:</b> {ev.attendance}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--bm-text-muted)' }}>
                    <b style={{ color: 'var(--bm-text)' }}>Phase action:</b> {meta.action}
                  </div>
                  <div>
                    <div style={{
                      fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
                      letterSpacing: '.5px', textTransform: 'uppercase' as const,
                      color: 'var(--bm-text-subtle)', marginBottom: 5,
                    }}>Pathogen-class risk priors</div>
                    <div style={{ display: 'grid', gap: 4 }}>
                      {ev.riskProfile.map((r, i) => (
                        <div key={i} style={{
                          display: 'grid',
                          gridTemplateColumns: '150px 50px 1fr', gap: 8, alignItems: 'center',
                          fontSize: 11.5,
                        }}>
                          <span style={{ color: 'var(--bm-text)' }}>{r.pathogenClass}</span>
                          <span style={{
                            fontFamily: 'var(--bm-font-mono)',
                            color: r.multiplier >= 4 ? 'var(--bm-threat-1)'
                                  : r.multiplier >= 2 ? 'var(--bm-threat-3)'
                                  : 'var(--bm-text-muted)',
                            fontWeight: 700,
                          }}>×{r.multiplier}</span>
                          <span style={{ color: 'var(--bm-text-muted)' }}>{r.rationale}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{
                    fontSize: 11.5, color: 'var(--bm-text-subtle)',
                    paddingTop: 8, borderTop: '1px solid var(--bm-border)',
                  }}>
                    ▦ {ev.precedent}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <PinToBriefButton compact item={{
                      id: `mass-gathering-${ev.id}`,
                      surface: '§7.27',
                      label: `${ev.name} (${ev.location}) · ${days > 0 ? `T-${days}d` : days < 0 ? `T+${-days}d` : 'TODAY'}`,
                      value: ev.attendance,
                      context: { iso3: ev.iso3, phase, nextDate: ev.nextDate, riskProfile: ev.riskProfile },
                    }} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{
        marginTop: 14, padding: '10px 13px',
        fontSize: 12.5, color: 'var(--bm-text-muted)', lineHeight: 1.5,
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderLeft: '3px solid var(--bm-accent, #6ea3ff)',
        borderRadius: 7,
      }}>
        <b style={{ color: 'var(--bm-text)' }}>The calendar is the leverage.</b>{' '}
        Mass-gathering events are PREDICTABLE — pre-warning at T-60/T-30/T-14
        means the response is ready, not reactive. Hajj 2002 (W135 meningococcal),
        Hajj 2012+ (MERS-CoV monitoring), and Tablighi Jamaat March 2020
        (COVID amplification) are the canonical lessons-learned. Per-event
        pathogen-class risk priors come from the cultural-risk register's
        mass-gathering domain (Phase 9.2a seed).
      </div>
    </div>
  );
}
